import { Box, Button, Divider } from '@mui/material';
import { FC } from 'react';

import {
  AbgTypography,
  mergeSx,
  paletteBase,
  useTranslation,
} from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useClearSession } from '../../hooks/use-clear-session';
import { bookingWidgetFormStoreSelector, userStoreSelector } from '../../store';
import { GlobalLink } from '../GlobalLink';

import {
  actionsContainerStyling,
  amazonCtaButtonStyling,
} from './amazon-account-actions-group.styling';
import { AmazonAccountActionsGroupProps } from './amazon-account-actions-group.types';

export const AmazonAccountActionsGroup: FC<AmazonAccountActionsGroupProps> = ({
  styleParts = {},
  amazonSettings,
  isMobile = false,
  handleClose = () => {},
  i18n,
}) => {
  const { navigationLinks } = usePageContext();
  const userSession = userStoreSelector.use.userSession();
  const t = useTranslation(i18n);
  const { clearSession } = useClearSession();
  const resetUserDataToDefault =
    bookingWidgetFormStoreSelector.use.resetUserDataToDefault?.();

  const { mobile, desktop } = styleParts.avisBudgetAmazonAccountTitle || {};
  const amazonTitleStyling = isMobile ? mobile : desktop;

  const { amazonLinked } = userSession || {};

  const onCreateAccount = () => {
    if (amazonSettings?.createAccountCtaLink) {
      globalThis.location.href = amazonSettings?.createAccountCtaLink;
    }
  };

  const handleLogout = async () => {
    await clearSession();
    resetUserDataToDefault?.();
    handleClose();

    // setting href because router.replace sometimes doesn't work in this case
    globalThis.location.href = navigationLinks.home;
  };

  return (
    <Box sx={actionsContainerStyling}>
      <AbgTypography
        component="div"
        sx={amazonTitleStyling || {}}
        data-testid="account-menu-title"
      >
        {amazonSettings?.menuHeaderDesktop}
      </AbgTypography>

      {!amazonLinked && (
        <>
          <Button
            variant="contained"
            onClick={onCreateAccount}
            sx={mergeSx(
              amazonCtaButtonStyling({ isMobile }),
              styleParts.createAvisBudgetAccountCTA,
            )}
          >
            {amazonSettings?.createAccountCtaText}
          </Button>

          <Box>
            <Divider>
              <AbgTypography variant="bodySmallRegular">
                {amazonSettings?.linkAmazonText}
              </AbgTypography>
            </Divider>
          </Box>

          <GlobalLink
            variant="text"
            dataTestId="sign-in-dialog-signin-btn"
            href="modal://login-modal"
            callbackFn={() => handleClose()}
            sx={mergeSx(
              amazonCtaButtonStyling({ color: paletteBase.black, isMobile }),
              styleParts.connectAvisBudgetAccountCTA,
            )}
          >
            {amazonSettings?.connectAccountCtaText}
          </GlobalLink>
        </>
      )}

      <Button
        onClick={() => void handleLogout()}
        variant="text"
        sx={mergeSx(
          amazonCtaButtonStyling({ color: paletteBase.black, isMobile }),
          styleParts.logoutAmazonCTA,
        )}
      >
        {t('amazonSettings.logout.label')}
      </Button>
    </Box>
  );
};
